// Columna izquierda del explorer de Cartera: búsqueda · estado · tienda · cobrador. Las opciones de
// tienda y cobrador salen de las filas presentes, así que el filtro nunca ofrece algo que no está.
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { AssignmentStatusEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStatusEnum';
import { cn } from '@/lib/cn';
import { countActiveFilters, describeAssignmentStatus } from '../logic';
import { ANY_FILTER, type AssignmentFiltersState, type FilterOption } from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

const STATUS_OPTIONS: FilterOption[] = Object.values(AssignmentStatusEnum).map((status) => ({
    value: status,
    label: describeAssignmentStatus(status),
}));

export const EMPTY_ASSIGNMENT_FILTERS: AssignmentFiltersState = {
    search: '',
    status: ANY_FILTER,
    storeId: ANY_FILTER,
    collectorUserId: ANY_FILTER,
};

export interface CollectionsFiltersPanelProps {
    filters: AssignmentFiltersState;
    onChange: (next: AssignmentFiltersState) => void;
    stores: readonly FilterOption[];
    collectors: readonly FilterOption[];
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function CollectionsFiltersPanel({
    filters,
    onChange,
    stores,
    collectors,
    collapsed,
    onToggleCollapsed,
}: CollectionsFiltersPanelProps) {
    const patch = (partial: Partial<AssignmentFiltersState>) => {
        onChange({ ...filters, ...partial });
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange(EMPTY_ASSIGNMENT_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={filters.search}
                        onChange={(event) => {
                            patch({ search: event.target.value });
                        }}
                        placeholder="Crédito o cobrador…"
                        aria-label="Buscar por crédito o cobrador"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {filters.search === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                patch({ search: '' });
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado</div>
                <select
                    value={filters.status}
                    onChange={(event) => {
                        patch({
                            status: event.target.value as AssignmentFiltersState['status'],
                        });
                    }}
                    aria-label="Estado"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* El combo existe con una sola opción: sirve para leer QUÉ tienda trae la lista,
                aunque el directorio no haya resuelto su nombre y la opción sea el id. */}
            {stores.length === 0 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Tienda</div>
                    <select
                        value={filters.storeId}
                        onChange={(event) => {
                            patch({ storeId: event.target.value });
                        }}
                        aria-label="Tienda"
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todas</option>
                        {stores.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {collectors.length === 0 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Cobrador</div>
                    <select
                        value={filters.collectorUserId}
                        onChange={(event) => {
                            patch({ collectorUserId: event.target.value });
                        }}
                        aria-label="Cobrador"
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todos</option>
                        {collectors.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </FiltersColumn>
    );
}
